import { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { useUserCreator } from "../../api/DataAPI";
import useStrings from "../../api/StringsAPI";

interface UserRecord {
  username: string;
  userType: 'ADMIN' | 'OPERATOR' | 'DEREGISTER';
  enabled: boolean;
  email?: string;
  organisation: { id: number; name: string };
}

interface NewUserDialogProps {
  visible: boolean;
  onHide: () => void;
  user: UserRecord | null;
  organizations: Array<{ id: number; name: string }>;
  onSuccess: () => void;
}

export default function NewUserDialog({ visible, onHide, user, organizations, onSuccess }: NewUserDialogProps) {
  const str = useStrings();
  const isEdit = user !== null;
  const createUserMutation = useUserCreator(isEdit);

  const [form, setForm] = useState({
    username: '',
    password: '',
    organisation: '' as string | number,
    userType: '',
    enabled: true,
    email: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const userTypeOptions = [
    { id: 'OPERATOR', name: str('operator') },
    { id: 'ADMIN', name: str('admin') }
  ];

  useEffect(() => {
    if (user) {
      setForm({
        username: user.username,
        password: '',
        organisation: user.organisation?.id || '',
        userType: user.userType,
        enabled: user.enabled,
        email: user.email || ''
      });
    } else {
      setForm({ username: '', password: '', organisation: '', userType: '', enabled: true, email: '' });
    }
    setErrors({});
    setMessage(null);
  }, [user, visible]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.username.trim()) newErrors.username = str('requiredField');
    if (!isEdit && !form.password.trim()) newErrors.password = str('requiredField');
    if (!isEdit && !form.organisation) newErrors.organisation = str('requiredField');
    if (!isEdit && !form.userType) newErrors.userType = str('requiredField');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = () => {
    if (!validate()) return;

    setMessage(null);

    const data = isEdit
      ? { username: form.username, enabled: form.enabled }
      : {
          username: form.username,
          password: form.password,
          organisation: String(form.organisation),
          userType: form.userType,
          email: form.email,
          enabled: form.enabled
        };

    createUserMutation.mutate(data as any, {
      onSuccess: () => {
        setMessage({ type: 'success', text: str(isEdit ? 'userUpdated' : 'userCreated') });
        onSuccess();
        setTimeout(onHide, 1500);
      },
      onError: () => {
        setMessage({ type: 'error', text: str('errorCreatingUser') });
      }
    });
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
      <Button label={str('cancel')} icon="pi pi-times" severity="secondary" onClick={onHide} />
      <Button
        label={str(isEdit ? 'modify' : 'create')}
        icon={isEdit ? "pi pi-check" : "pi pi-plus"}
        loading={createUserMutation.isLoading}
        onClick={onSubmit}
      />
    </div>
  );

  return (
    <Dialog
      header={str('newUserTitle')}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: 600 }}
      modal
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 15 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('username')}</label>
          <InputText
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            disabled={isEdit}
            className={errors.username ? 'p-invalid' : ''}
          />
          {errors.username && <small className="p-error">{errors.username}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('password')}</label>
          <Password
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            feedback={false}
            toggleMask
            className={errors.password ? 'p-invalid' : ''}
          />
          {errors.password && <small className="p-error">{errors.password}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('organization')}</label>
          <Dropdown
            value={form.organisation}
            options={organizations}
            onChange={(e) => setForm({ ...form, organisation: e.value })}
            optionLabel="name"
            optionValue="id"
            filter
            disabled={isEdit}
            className={errors.organisation ? 'p-invalid' : ''}
            placeholder={str('organization')}
          />
          {errors.organisation && <small className="p-error">{errors.organisation}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('userType')}</label>
          <Dropdown
            value={form.userType}
            options={userTypeOptions}
            onChange={(e) => setForm({ ...form, userType: e.value })}
            optionLabel="name"
            optionValue="id"
            disabled={isEdit}
            className={errors.userType ? 'p-invalid' : ''}
            placeholder={str('userType')}
          />
          {errors.userType && <small className="p-error">{errors.userType}</small>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label>{str('email')}</label>
          <InputText
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            disabled={isEdit}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', alignSelf: 'end', paddingBottom: 5 }}>
          <Checkbox
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.checked ?? false })}
          />
          <label>{str('statusActive')}</label>
        </div>
      </div>

      {message && (
        <Message severity={message.type} text={message.text} style={{ marginTop: 15, width: '100%' }} />
      )}
    </Dialog>
  );
}
